/**
 * Performance Monitoring Utility
 * Tracks and reports performance metrics to ensure animations don't cause latency
 */

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  droppedFrames: number;
  memoryUsage?: number;
  timestamp: number;
}

class PerformanceMonitor {
  private frameCount = 0;
  private lastTime = performance.now();
  private fps = 60;
  private frameTime = 16.67; // 60fps = 16.67ms per frame
  private droppedFrames = 0;
  private isMonitoring = false;
  private rafId: number | null = null;
  private metricsHistory: PerformanceMetrics[] = [];
  private readonly maxHistorySize = 100;
  private readonly warningThreshold = 30; // FPS below this triggers warning
  private readonly criticalThreshold = 20; // FPS below this triggers critical warning

  /**
   * Start monitoring performance
   */
  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.lastTime = performance.now();
    this.frameCount = 0;
    this.droppedFrames = 0;
    
    const monitor = (currentTime: number) => {
      const delta = currentTime - this.lastTime;
      this.frameCount++;
      
      // Calculate FPS
      if (delta >= 1000) {
        this.fps = Math.round((this.frameCount * 1000) / delta);
        this.frameTime = delta / this.frameCount;
        
        // Detect dropped frames (frame time > 20ms indicates < 50fps)
        if (this.frameTime > 20) {
          this.droppedFrames += Math.floor((this.frameTime - 16.67) / 16.67);
        }
        
        // Log warning if performance is poor
        if (this.fps < this.criticalThreshold) {
          console.warn(`⚠️ Critical: FPS dropped to ${this.fps} (target: 60)`);
        } else if (this.fps < this.warningThreshold) {
          console.warn(`⚠️ Warning: FPS dropped to ${this.fps} (target: 60)`);
        }
        
        // Store metrics
        const metrics: PerformanceMetrics = {
          fps: this.fps,
          frameTime: this.frameTime,
          droppedFrames: this.droppedFrames,
          memoryUsage: this.getMemoryUsage(),
          timestamp: currentTime
        };
        
        this.metricsHistory.push(metrics);
        if (this.metricsHistory.length > this.maxHistorySize) {
          this.metricsHistory.shift();
        }
        
        // Reset counters
        this.frameCount = 0;
        this.droppedFrames = 0;
        this.lastTime = currentTime;
      }
      
      if (this.isMonitoring) {
        this.rafId = requestAnimationFrame(monitor);
      }
    };
    
    this.rafId = requestAnimationFrame(monitor);
  }

  /**
   * Stop monitoring performance
   */
  stopMonitoring() {
    this.isMonitoring = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Get current FPS
   */
  getFPS(): number {
    return this.fps;
  }

  /**
   * Get current frame time in milliseconds
   */
  getFrameTime(): number {
    return this.frameTime;
  }

  /**
   * Get performance metrics history
   */
  getMetricsHistory(): PerformanceMetrics[] {
    return [...this.metricsHistory];
  }

  /**
   * Get average FPS over last N seconds
   */
  getAverageFPS(seconds: number = 5): number {
    const cutoff = performance.now() - (seconds * 1000);
    const recentMetrics = this.metricsHistory.filter(m => m.timestamp > cutoff);
    
    if (recentMetrics.length === 0) return this.fps;
    
    const sum = recentMetrics.reduce((acc, m) => acc + m.fps, 0);
    return Math.round(sum / recentMetrics.length);
  }

  /**
   * Check if performance is acceptable
   */
  isPerformanceAcceptable(): boolean {
    return this.fps >= this.warningThreshold;
  }

  /**
   * Get memory usage (if available)
   */
  private getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize / 1048576; // Convert to MB
    }
    return undefined;
  }

  /**
   * Get performance report
   */
  getReport() {
    const avgFPS = this.getAverageFPS(10);
    const recentMetrics = this.metricsHistory.slice(-10);
    const avgFrameTime = recentMetrics.length > 0
      ? recentMetrics.reduce((acc, m) => acc + m.frameTime, 0) / recentMetrics.length
      : this.frameTime;

    return {
      currentFPS: this.fps,
      averageFPS: avgFPS,
      currentFrameTime: this.frameTime,
      averageFrameTime: avgFrameTime,
      isAcceptable: this.isPerformanceAcceptable(),
      memoryUsage: this.getMemoryUsage(),
      metricsCount: this.metricsHistory.length
    };
  }
}

// Singleton instance
const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  // Start monitoring after page load
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        performanceMonitor.startMonitoring();
        console.log('📊 Performance monitoring started');
      }, 1000);
    });
  }
}

export default performanceMonitor;
